import React, { useState } from 'react';
import { InlineSpinner } from '../../common/loading';

import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  alpha,
  Autocomplete,
  Chip,
Button,
  FormHelperText,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption } from '../../../services/api';
import { FormData } from './types';
import { getProjectStatusColor, filterProjectsByStatus } from './utils';

interface PublicTestimonialFormProps {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | number | null | File | File[] | string[]) => void;
  projectOptions: ProjectDropdownOption[];
  projectsLoading: boolean;
  projectStatusFilter: string | null;
  onProjectStatusFilterChange: (filter: string | null) => void;
  selectedProject: ProjectDropdownOption | null;
  onProjectSelect: (project: ProjectDropdownOption | null) => void;
  isFormDisabled: boolean;
}

export const PublicTestimonialForm: React.FC<PublicTestimonialFormProps> = ({
  formData,
  onInputChange,
  projectOptions,
  projectsLoading,
  projectStatusFilter,
  onProjectStatusFilterChange,
  selectedProject,
  onProjectSelect,
  isFormDisabled,
}) => {
  const filteredProjectOptions = filterProjectsByStatus(projectOptions, projectStatusFilter);
  
  // Track which fields have been touched (focused and blurred)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const handleBlur = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
  };

  const shouldShowError = (fieldName: string, isEmpty: boolean) => {
    return touchedFields.has(fieldName) && isEmpty;
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isFormDisabled ? alpha(SEARCE_COLORS.neutral_200, 0.5) : SEARCE_COLORS.cardBackground,
      transition: 'background-color 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isFormDisabled ? SEARCE_COLORS.border : SEARCE_COLORS.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: SEARCE_COLORS.primary,
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: SEARCE_COLORS.primary,
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Project Selection Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
            mb: 2,
          }}
        >
          Project Selection
        </Typography>
      </Grid>

      {/* Project Code - Dynamic Dropdown */}
      <Grid item xs={12}>
        <Autocomplete
          options={filteredProjectOptions}
          getOptionLabel={(option) => option.display_label}
          value={selectedProject}
          onChange={(_, newValue) => {
            onProjectSelect(newValue);
            if (!newValue) {
              handleBlur('projectCode');
            }
          }}
          onBlur={() => handleBlur('projectCode')}
          loading={projectsLoading}
          isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Code"
              required
              helperText="Select from your projects"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {projectsLoading ? <InlineSpinner size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.project_code}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
                    {option.project_code}
                  </Typography>
                  {option.status && (
                    <Chip
                      label={option.status}
                      size="small"
                      sx={{
                        backgroundColor: getProjectStatusColor(option.status),
                        color: SEARCE_COLORS.white,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary }}>
                  {option.project_name}
                </Typography>
              </Box>
            </Box>
          )}
          disabled={isFormDisabled}
        />
        {shouldShowError('projectCode', !selectedProject) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Project Code is required
          </FormHelperText>
        )}
      </Grid>

      {/* Auto-filled fields */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2.5,
            border: `1px solid ${SEARCE_COLORS.border}`,
            backgroundColor: SEARCE_COLORS.cardBackground,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              mb: 2.5,
            }}
          >
            Project Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Code"
                value={formData.projectCode || ''}
                disabled
                helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.accountName || ''}
                disabled
                helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email || ''}
                disabled
                helperText="Auto-filled from your account"
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Public Testimonial Details Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
            mb: 2,
          }}
        >
          Public Testimonial Details
        </Typography>
      </Grid>

      {/* Customer POC Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Customer POC Name"
          required
          value={formData.customerPocName || ''}
          onChange={(e) => onInputChange('customerPocName', e.target.value)}
          onBlur={() => handleBlur('customerPocName')}
          disabled={isFormDisabled}
          sx={textFieldSx}
        />
        {shouldShowError('customerPocName', !(formData.customerPocName || '').trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Customer POC Name is required
          </FormHelperText>
        )}
      </Grid>

      {/* Customer POC Designation */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Customer POC Designation"
          required
          value={formData.customerPocDesignation || ''}
          onChange={(e) => onInputChange('customerPocDesignation', e.target.value)}
          onBlur={() => handleBlur('customerPocDesignation')}
          disabled={isFormDisabled}
          sx={textFieldSx}
        />
        {shouldShowError('customerPocDesignation', !(formData.customerPocDesignation || '').trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Customer POC Designation is required
          </FormHelperText>
        )}
      </Grid>

      {/* Date Testimony Received */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Date Testimony Received"
          type="date"
          value={formData.dateTestimonyReceived || ''}
          onChange={(e) => onInputChange('dateTestimonyReceived', e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toLocaleDateString('en-CA') }}
          disabled={isFormDisabled}
          helperText="Cannot be a future date"
          sx={textFieldSx}
        />
      </Grid>

      {/* Link to the Public Testimonial */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Link to the Public Testimonia"
          required
          value={formData.publicTestimonialLink || ''}
          onChange={(e) => onInputChange('publicTestimonialLink', e.target.value)}
          onBlur={() => handleBlur('publicTestimonialLink')}
          placeholder="https://..."
          disabled={isFormDisabled}
          sx={textFieldSx}
        />
        {shouldShowError('publicTestimonialLink', !(formData.publicTestimonialLink || '').trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Link to the Public Testimonial is required
          </FormHelperText>
        )}
      </Grid>

      {/* Upload supporting documents */}
      <Grid item xs={12}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: SEARCE_COLORS.textPrimary,
            mb: 1,
          }}
        >
          Upload supporting documents (e.g., screenshots, audio recordings, email threads)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            disabled={isFormDisabled}
            sx={{
              borderColor: formData.publicTestimonialFile ? SEARCE_COLORS.primary : SEARCE_COLORS.border,
              color: formData.publicTestimonialFile ? SEARCE_COLORS.primary : SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
              },
            }}
          >
            {formData.publicTestimonialFile ? 'Change file' : 'Add file'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.mp3,.mp4,.wav,.m4a"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                onInputChange('publicTestimonialFile', file);
                handleBlur('publicTestimonialFile');
              }}
            />
          </Button>
          <Typography 
            variant="body2" 
            sx={{ 
              color: formData.publicTestimonialFile ? SEARCE_COLORS.textPrimary : SEARCE_COLORS.textSecondary,
              fontWeight: formData.publicTestimonialFile ? 500 : 400,
            }}
          >
            {formData.publicTestimonialFile ? formData.publicTestimonialFile.name : 'No file chosen'}
          </Typography>
        </Box>
        <FormHelperText sx={{ mt: 1 }}>
          Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG, MP3, MP4, WAV, M4A (Max 10MB)
        </FormHelperText>
        {shouldShowError('publicTestimonialFile', !formData.publicTestimonialFile) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Upload supporting documents is required
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  );
};

